<script>
  import _ from 'lodash'
  import moment from 'moment'
  import JWT from 'jsonwebtoken'

  //       _ ____   ___  _   _  __     _____ _______        _______ ____
  //      | / ___| / _ \| \ | | \ \   / /_ _| ____\ \      / / ____|  _ \
  //   _  | \___ \| | | |  \| |  \ \ / / | ||  _|  \ \ /\ / /|  _| | |_) |
  //  | |_| |___) | |_| | |\  |   \ V /  | || |___  \ V  V / | |___|  _ <
  //   \___/|____/ \___/|_| \_|    \_/  |___|_____|  \_/\_/  |_____|_| \_\
  //

  const _REMOVE_QUOTES = str => str.replace(/^"/, '').replace(/"$/, '')

  const _MAP_LOAD_CHILDREN = new Map()

  export default {
    name: 'JsonViewer',
    props: {
      id: {
        type: String,
        default: 'v-json-viewer'
      },
      options: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        show: false,
        allNodes: [],
        openNodes: [],
        command: [],
        customCommands: [],
        valueWidth: 500,
        searchInput: '',
        caseSensitive: false,
        showPickerDate: false,
        pickerDate: null,
        sortByType: false,
        fullscreen: false
      }
    },

    computed: {
      treeItems() {
        const options = {...this.options}
        const items = []
        _.each(options, (obj, key) => {
          items.push(this.mapItem(obj, key, 'root'))
        })
        const order = this.sortByType ? ['type', 'label'] : ['label']
        return _.orderBy(items, order)
      },
      commandItems() {
        return [
          {value: 'expandAll', text: 'Expand all'},
          {value: 'collapseAll', text: 'Collapse all'},
          {value: 'clearSearch', text: 'Clear search'},
          {value: 'sortByName', text: 'Sort by name'},
          {value: 'sortByType', text: 'Sort by type'},
          {value: 'maximize', text: 'Maximize window'},
          {value: 'minimize', text: 'Minimize window'}
        ].concat(this.customCommands)
      },
      filter() {
        return this.caseSensitive
          ? (item, search, textKey) => item[textKey].indexOf(search) > -1
          : undefined
      }
    },
    watch: {
      show(val) {
        if (!val) this.close()
      },
      openNodes() {
        // calc max width of tree nodes
        // this.$nextTick(() => {
        //   const nodes = this.$refs.tree.$el.querySelectorAll('.v-treeview-node__label')
        //   let maxLeft = 0
        //   _.each(nodes, node => {
        //     const rect = node.getBoundingClientRect()
        //     maxLeft = Math.max(maxLeft, rect.right)
        //   })
        //   if (maxLeft > 400) this.valueWidth = 450
        //   if (maxLeft > 500) this.valueWidth = 400
        //   if (this.$vuetify.breakpoint.name === 'xs') this.valueWidth = 0
        // })
      },
      fullscreen() {
        this.calcValueWidth()
      },
      '$vuetify.breakpoint.name'() {
        console.log('breakpoint')
      }
    },

    beforeMount() {
      const $app = document.querySelector('#app') || document.body || document.getElementsByTagName('body')[0]
      if ($app) $app.appendChild(this.$el)
      this.show = true
    },
    mounted() {
      this.allNodes = []
      this.$nextTick(() => {
        const $el = document.querySelector(`#${this.id}`)
        if ($el) $el.close = this.close.bind(this)
        this.$options.onMounted($el)
        this.$autofocus({ref: 'command'}, {initDelay: 500})
      })
    },

    methods: {
      calcValueWidth() {
        if (this.$vuetify.breakpoint.name === 'xs') {
          this.valueWidth = 0
          return
        }

        this.$nextTick(() => {
          const treeRect = this.$refs.tree.$el.getBoundingClientRect()
          this.valueWidth = treeRect.width - 400
        })
      },

      mapItem(obj, label, id) {
        label = `${label}`
        id = `${id}_${label}`
        const {type, value, icon, cls, size, hasChildren} = this.typedef(obj, label)
        // NOTE to show open icon and trigger loadChildren we must set an empty array
        const children = hasChildren ? _MAP_LOAD_CHILDREN.get(id) || [] : this.children(obj, id, type)
        if (!_.isEmpty(children)) this.allNodes.push(id)
        return {id, label, type, icon, value, cls, size, children, hasChildren}
      },
      children(obj, id, type) {
        if (!_.isObject(obj)) return

        const items = []
        _.each(obj, (value, key) => {
          items.push(this.mapItem(value, key, id))
        })
        if (type === 'array') return items // do not sort childs of array (1, 10, 11, 2)
        const order = this.sortByType ? ['type', 'label'] : ['label']
        return _.orderBy(items, order)
      },
      typedef(obj, label) {
        let value = `${obj}`
        let icon = ''
        let cls = ''
        let type = ''
        let size = 0
        let hasChildren = false

        if (_.isNull(obj) || _.isUndefined(obj)) {
          type = _.isNull(obj) ? 'null' : 'undefined'
          icon = 'mdi-alert-box-outline'
          cls = 'v-jv--null'
        }
        else if (_.isString(obj)) {
          size = value.length
          type = 'string'
          icon = 'mdi-code-string'
          value = `"${value}"`
          cls = 'v-jv--string'
          if (this.isColor(obj)) {
            type = 'color'
            icon = 'mdi-palette'
            value = `${obj}`
            cls = 'v-jv--color'
          }
          else if (this.isJSONString(obj)) {
            icon = 'mdi-code-braces'
            value = `${obj}`
            hasChildren = true
          }
          else {
            const mom = moment(obj, moment.ISO_8601)
            if (mom.isValid()) {
              type = 'date'
              icon = 'mdi-calendar'
              // value = `"${value}"`
              cls = 'v-jv--date'
            }
          }
        }
        else if (_.isBoolean(obj)) {
          type = 'boolean'
          icon = obj ? 'mdi-check-box-outline' : 'mdi-checkbox-blank-outline'
          cls = 'v-jv--boolean'
        }
        else if (_.isNumber(obj)) {
          type = 'number'
          icon = 'mdi-numeric'
          cls = 'v-jv--numeric'
        }
        else if (_.isArray(obj)) {
          type = 'array'
          icon = 'mdi-code-brackets'
          value = this.$json.stringify(obj)
          value = `(${_.size(obj)}) ${value}`
        }
        else if (_.isObject(obj)) {
          type = 'object'
          icon = 'mdi-code-braces'
          value = this.$json.stringify(obj)
        }
        if (!type) console.log('obj', obj)

        if (/_token$/.test(label)) {
          type = 'token'
          icon = 'mdi-key'
          cls = 'v-jv--token v-jv--string'
          hasChildren = true
        }

        return {type, value, icon, cls, size, hasChildren}
      },
      loadChildren(item) {
        if (this.isJWTToken(item)) {
          const decoded = JWT.decode(_REMOVE_QUOTES(item.value))
          const children = this.children(decoded, item.id)
          if (_.isEmpty(children)) return

          _MAP_LOAD_CHILDREN.set(item.id, children)
        }
        else if (this.isJSONString(item.value)) {
          const children = this.children(this.$json.parse(item.value), item.id)
          if (_.isEmpty(children)) return

          _MAP_LOAD_CHILDREN.set(item.id, children)
        }
        else console.log('loadChildren?', item)
      },

      isColor(value) {
        if (!value) return false
        if (/^rgb\(.*\)$/.test(value)) return true
        if (/^rgba\(.*\)$/.test(value)) return true
        // console.log('value', typeof value, value)
        return value && value.charAt(0) === '#' && value.length === 7
      },
      isJSONString(value) {
        if (!_.isString(value)) return false
        if (!/^{/.test(value) || !/}$/.test(value)) return false
        return true
      },
      isJWTToken(item) {
        return /_token$/.test(item.label)
      },

      deleteItem(item) {
        console.log('item', item)
      },
      exportItem() {
        console.log('this.options.export', JSON.stringify(this.options.export, null, 2))
      },

      close() {
        if (this.open) this.open = false
        setTimeout(() => {
          this.$options.onClose()
          this.$destroy()
          removeElement(this.$el)
        }, 400) // wait for close animation
      },

      onAction() {
        this.$options.onAction()
      },
      onCommand(command) {
        if (!command) return

        this.command = []

        if (/^\//.test(command)) {
          this.searchInput = command.substr(1)
          this.customCommands.push({value: command, text: `Suche nach "${this.searchInput}"`})
          return
        }

        if (_.isString(command)) {
          const fcommand = _.find(this.commandItems, c => c.value.toLowerCase().search(command) >= 0)
          if (fcommand) command = fcommand
        }

        switch(command.value) {
          case 'expandAll':
            this.openNodes = this.allNodes
            break
          case 'collapseAll':
            this.openNodes = []
            break
          case 'clearSearch':
            this.searchInput = ''
            break
          case 'sortByName':
            this.sortByType = false
            break
          case 'sortByType':
            this.sortByType = true
            break
          case 'minimize':
            this.fullscreen = false
            break
          case 'maximize':
            this.fullscreen = true
            break
          default:
            console.warn('unknown command', command.value || command)
        }
      },
      onInput(command) {
        // console.log('oninput command', command)
      },
      onIcon(item) {
        // console.log('item', item)
        if (item.type === 'date') {
          const date = item.value.replace(/"/g, '')
          this.pickerDate = moment(date).utc().local().format('YYYY-MM-DD')
          this.showPickerDate = true
        }
      }
    }
  }

  function removeElement(el) {
    typeof el.remove !== 'undefined' ? el.remove() : el.parentNode.removeChild(el)
  }
</script>

<template>
  <VDialog :id="id" ref="dialog" v-model="show" persistent :fullscreen="fullscreen" :max-width="800">
    <VSheet class="v-json-viewer__sheet pa-0 fill-height d-flex flex-column" dark>
      <VToolbar dense>
        <VToolbarTitle>
          <VSubheader class="headline justify-center animated bounceIn delay-1s">JSON</VSubheader>
        </VToolbarTitle>
        <VSpacer/>
        <VBtn small icon class="mr-0" @click.left="close">
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbar>

      <!-- Bottom Autocomplete: Command line -->
      <VSheet class="v-json-viewer__command pa-0 px-4 d-flex flex-row align-center" light max-width="320px">
        <VCombobox
            ref="command"
            v-model="command"
            :items="commandItems"
            label="command"
            placeholder="type..."
            :spellcheck="false"
            :menu-props="{top: true, contentClass: 'v-json-viewer__command-menu'}"
            dense
            hide-details
            solo
            flat
            single-line
            hide-no-data
            class="black"
            @change="onCommand"
            @input="onInput"
        />
        <VSpacer/>
        <VBtn small icon style="margin:2px" @click="openNodes = []">
          <VIcon small>mdi-chevron-up</VIcon>
        </VBtn>
        <VBtn small icon style="margin:2px" @click="openNodes = allNodes">
          <VIcon small>mdi-chevron-down</VIcon>
        </VBtn>
        <VBtn small icon style="margin:2px" @click="sortByType = !sortByType">
          <VIcon small>mdi-sort</VIcon>
        </VBtn>
      </VSheet>

      <!-- Treeview -->
      <VSheet class="pa-4" dark>
        <VTreeview
            ref="tree"
            :items="treeItems"
            item-text="label"
            :open.sync="openNodes"
            :search="searchInput"
            :filter="filter"
            :load-children="loadChildren"
            color="#CCC"
            activatable
            :multiple-active="false"
            dense
            class="v-json-viewer__tree scrollable-y"
        >
          <template v-slot:prepend="{ item }">
            <VIcon v-if="item && item.icon" small class="v-json-viewer__icon" :class="item.cls" @click.left="onIcon(item)">{{ item.icon }}</VIcon>
          </template>
          <template v-slot:append="{ item, open }">
            <div v-if="!open" class="v-json-viewer__value caption pr-4" :class="item.cls" :style="{'width': valueWidth ? `${valueWidth}px` : ''}">
              <span v-if="item.type === 'color'" class="v-json-viewer__color" :style="{'backgroundColor': item.value}"></span>
              {{ item.value }}
            </div>
          </template>
          <template v-slot:label="{ item, active }">
            <span>{{ item.label }}</span>
            <span v-if="active && item.size" class="px-2 font-weight-regular">({{ item.size }})</span>
            <VIcon v-if="active" small style="float:right" @click.left.stop="exportItem(item)">mdi-export</VIcon>
            <VIcon v-if="active" small style="float:right" @click.left.stop="deleteItem(item)">mdi-trash-can-outline</VIcon>
          </template>
        </VTreeview>
      </VSheet>
    </VSheet>

    <VDialog v-model="showPickerDate" width="290px">
      <VDatePicker v-model="pickerDate" show-current/>
    </VDialog>
  </VDialog>
</template>

<style scoped>
  .v-json-viewer__sheet {
    position: relative;
  }
  .v-json-viewer__command {
    position: absolute;
    right: 10px;
    bottom: 8px;
    left: 10px;
    z-index: 10;
    height: 40px;
  }
  .v-json-viewer__tree {
    height: 640px;
    margin-bottom: 40px;
    font-size: 80%;
    font-weight: 600;
  }
  .v-dialog--fullscreen .v-json-viewer__tree {
    height: calc(100vh - 120px);
  }
  .v-treeview-node__label > button {
    margin-left: 4px;
  }
  .v-json-viewer__value {
    overflow: hidden;
    font-weight: 600;
    text-overflow: ellipsis;
    word-break: break-all;
    white-space: nowrap;
  }
  .v-json-viewer__value.v-jv--numeric {
    font-weight: 600;
  }
  .v-jv--numeric {
    color: #f08d49;
  }
  .v-jv--string {
    color: #7ec699;
  }
  .v-jv--null {
    font-weight: 600;
    color: lightcoral;
  }
  .v-jv--date {
    color: violet;
  }

  .v-json-viewer__color {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 2px;
    vertical-align: text-bottom;
    border: 1px solid white;
  }

  .v-treeview--dense /deep/ .v-treeview-node__root {
    min-height: 24px;
  }

  /* hide empty node icon placeholder, has min-width 24px */
  .v-treeview--dense.v-tmp /deep/ .v-treeview-node__prepend:empty {
    display: none;
  }
</style>
<style>
  .v-json-viewer__command-menu .v-list--dense .v-list-item {
    min-height: 24px;
  }
</style>
